import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Eye, EyeOff, RefreshCw, Plus, Minus, Check, X, AlertTriangle, Copy, ExternalLink, Loader2 } from 'lucide-react';
import copy from "copy-to-clipboard";
import { userService } from '../../_services/user.service';

function GlicApi({
    text,
    protectedData,
    glicData,
    glicIps,
    closeGlicPopup,
    ipListUpdated,
    openApiAddModal
}) {
    const [loading, setLoading] = useState(false);
    const [clientSecret, setClientSecret] = useState(protectedData);
    const [eyeIcon, setEyeIcon] = useState(false);
    const [copyTooltip, setCopyTooltip] = useState('');
    const [successIpAddress, setSuccessIpAddress] = useState('');
    const [errorIpAddress, setErrorIpAddress] = useState('');
    const [bearerToken, setBearerToken] = useState(null);
    const [showBearerToken, setShowBearerToken] = useState(false);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [tokenError, setTokenError] = useState('');
    const [tokenCopyTooltip, setTokenCopyTooltip] = useState('Show/Copy Token');
    const [tokenRegenTooltip, setTokenRegenTooltip] = useState('Regenerate Token. Your old token(s) will be lost after this!');
    const [tokenList, setTokenList] = useState([]);
    const [tokenListLoading, setTokenListLoading] = useState(false);
    const [bearerAvailable, setBearerAvailable] = useState(true);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const hosturl = window.location.host;
    const isRxTheme = useMemo(() => {
        const domain = hosturl.split('.').slice(-2).join('.').toLowerCase();
        return domain === "rxhacker.com" || domain === "localhost:5464" || hosturl.toLowerCase() === "tempfilter.23byt.es";
    }, [hosturl]);

    const showWhitelistedSection = useMemo(() => {
        return ['staging.glichealth.com', 'reporting.nulifespanrx.com', 'reporting.glichealth.com', 'dev.glichealth.com', 'localhost:5464', 'tempfilter.23byt.es'].includes(hosturl);
    }, [hosturl]);

    const isDynamicIP = useMemo(() => {
        return glicIps?.length > 0 && glicIps[0].sType === 'Dynamic IP';
    }, [glicIps]);

    const loadTokenList = useCallback(async () => {
        try {
            setTokenListLoading(true);
            const clientId = glicData?.[0]?.client_Id || '';
            const basicAuthToken = 'Basic ' + btoa('23bytes1:vtak2e8Klk474Ziyr1HW');

            const response = await userService.listTokens(clientId, basicAuthToken);
            if (response?.tokens?.length > 0) {
                setTokenList(response.tokens);
                setBearerAvailable(true);
            } else {
                setTokenList([]);
                setBearerAvailable(false);
            }
        } catch (error) {
            setTokenList([]);
            setBearerAvailable(false);
        } finally {
            setTokenListLoading(false);
        }
    }, [glicData]);

    useEffect(() => {
        loadTokenList();
    }, [loadTokenList]);

    const handleEye = useCallback(() => {
        try {
            if (!eyeIcon) {
                copy(glicData?.[0]?.client_Secret || '');
                setClientSecret(glicData?.[0]?.client_Secret || '');
                setEyeIcon(true);
                setCopyTooltip('Copied');
                setTimeout(() => setCopyTooltip(''), 1000);
            } else {
                setClientSecret(protectedData);
                setEyeIcon(false);
            }
        } catch (error) {
        }
    }, [eyeIcon, glicData, protectedData]);

    const handleApiAddModal = useCallback(() => {
        closeGlicPopup?.();
        openApiAddModal?.();
    }, [closeGlicPopup, openApiAddModal]);

    const handleGenerateToken = useCallback(async () => {
        if (bearerToken) {
            setShowBearerToken(false);
            return;
        }

        setTokenLoading(true);
        setTokenError('');

        try {
            const clientId = glicData?.[0]?.client_Id || '';
            const clientSecretVal = glicData?.[0]?.client_Secret || '';
            const basicAuthToken = 'Basic ' + btoa('23bytes1:vtak2e8Klk474Ziyr1HW');

            const response = await userService.generatebearerToken(clientId, clientSecretVal, basicAuthToken);
            if (response?.accessToken) {
                setBearerToken(response.accessToken);
                setShowBearerToken(false);
                loadTokenList();
            } else {
                setTokenError('Failed to generate token. Please try again.');
            }
        } catch (error) {
            setTokenError('Failed to generate token. Please try again.');
        } finally {
            setTokenLoading(false);
        }
    }, [bearerToken, glicData, loadTokenList]);

    const handleRegenerateToken = useCallback(async () => {
        setTokenLoading(true);
        setTokenError('');

        try {
            const clientId = glicData?.[0]?.client_Id || '';
            const clientSecretVal = glicData?.[0]?.client_Secret || '';
            const basicAuthToken = 'Basic ' + btoa('23bytes1:vtak2e8Klk474Ziyr1HW');

            const response = await userService.regeneratebearerToken(clientId, clientSecretVal, basicAuthToken);
            if (response?.accessToken) {
                setBearerToken(response.accessToken);
                setShowBearerToken(false);
                setTokenRegenTooltip('Token Regenerated!');
                loadTokenList();
                setTimeout(() => setTokenRegenTooltip('Regenerate Token. Your old token(s) will be lost after this!'), 2000);
            } else {
                setTokenError('Failed to regenerate token. Please try again.');
            }
        } catch (error) {
            setTokenError('Failed to regenerate token. Please try again.');
        } finally {
            setTokenLoading(false);
        }
    }, [glicData, loadTokenList]);

    const handleCopyToken = useCallback(() => {
        if (bearerToken) {
            copy(bearerToken);
            setTokenCopyTooltip('Copied!');
            setTimeout(() => {
                setTokenCopyTooltip('Hide Token');
                setShowBearerToken(!showBearerToken);
            }, 1000);
        }
    }, [bearerToken, showBearerToken]);

    const removeWhiteListedIPs = useCallback(async (ipObjectId) => {
        try {
            const apidata = await userService.ResetWhiteListIP(ipObjectId, userdata?.email, '');
            if (apidata?.[0]?.code === '1') {
                setSuccessIpAddress(apidata[0].message);
                setErrorIpAddress('');
            } else {
                setErrorIpAddress(apidata?.[0]?.message || 'Error removing IP');
                setSuccessIpAddress('');
            }
            ipListUpdated?.();
            setTimeout(() => {
                setSuccessIpAddress('');
                setErrorIpAddress('');
            }, 3000);
        } catch (error) {
            setErrorIpAddress('Error removing IP');
        }
    }, [userdata, ipListUpdated]);

    const maskText = useCallback((text) => {
        if (!text || text.length <= 5) return text;
        const start = text.slice(0, 3);
        const end = text.slice(-2);
        return `${start}***************${end}`;
    }, []);

    const formatPrettyDate = useCallback((dateStr) => {
        const date = new Date(dateStr);
        const day = date.getDate();
        const suffix = day % 10 === 1 && day !== 11 ? "st" :
            day % 10 === 2 && day !== 12 ? "nd" :
            day % 10 === 3 && day !== 13 ? "rd" : "th";
        const month = date.toLocaleString('en-GB', { month: 'short' });
        const year = date.getFullYear();
        return `${day}${suffix} ${month} ${year}`;
    }, []);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closeGlicPopup?.();
        }
    }, [closeGlicPopup]);

    const apiData = glicData?.[0] || {};

    return (
        <TooltipProvider>
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : 
                    <div className="border border-border rounded-lg overflow-hidden">
                        <Table>
                            <TableBody>
                            {/* API Documentation */}
                            <TableRow>
                                <TableCell className="font-medium w-1/3 border-r border-dashed px-2 py-3">API Documentation</TableCell>
                                <TableCell className="px-2 py-3">
                                    <a 
                                        href="https://documenter.getpostman.com/view/7488757/UVC9i6FE" 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="text-primary hover:underline inline-flex items-center gap-1"
                                    >
                                        Click Here to Open <ExternalLink className="h-3 w-3" />
                                    </a>
                                </TableCell>
                            </TableRow>

                            {/* Client ID */}
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Client ID</TableCell>
                                <TableCell className="font-mono text-sm px-2 py-3">{apiData.client_Id}</TableCell>
                            </TableRow>

                            {/* Client Secret */}
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Client Secret</TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm">{clientSecret}</span>
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleEye}>
                                            {eyeIcon ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                        </Button>
                                        {copyTooltip && <Badge variant="secondary">{copyTooltip}</Badge>}
                                    </div>
                                </TableCell>
                            </TableRow>

                            {/* Token Based Auth */}
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Token Based Auth</TableCell>
                                <TableCell>
                                    {bearerAvailable ? (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {bearerToken && (
                                                <>
                                                    <span className="font-mono text-sm">
                                                        {showBearerToken ? maskText(bearerToken) : '********************'}
                                                    </span>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCopyToken}>
                                                        {showBearerToken ? <Eye className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                                    </Button>
                                                </>
                                            )}
                                            <Tooltip delayDuration={0}>
                                                <TooltipTrigger asChild>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="h-8 w-8" 
                                                        onClick={handleRegenerateToken}
                                                        disabled={tokenLoading}
                                                    >
                                                        {tokenLoading ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">
                                                    <div className="flex items-center gap-1">
                                                        <AlertTriangle className="h-3 w-3" />
                                                        <span>{tokenRegenTooltip}</span>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        </div>
                                    ) : (
                                        <Button onClick={handleGenerateToken} disabled={tokenLoading} size="sm">
                                            {tokenLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Generate Token
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Token Error */}
                            {tokenError && (
                                <TableRow>
                                    <TableCell className="border-r border-dashed px-2 py-3"></TableCell>
                                    <TableCell>
                                        <p className="text-sm text-destructive">{tokenError}</p>
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* Token Info */}
                            {tokenListLoading ? (
                                <TableRow>
                                    <TableCell className="border-r border-dashed px-2 py-3"></TableCell>
                                    <TableCell>
                                        <span className="text-sm text-muted-foreground">Loading token information...</span>
                                    </TableCell>
                                </TableRow>
                            ) : tokenList.length > 0 && (
                                tokenList.map((token, index) => (
                                    <TableRow key={token.tokenId}>
                                        <TableCell className="font-medium border-r border-dashed px-2 py-3">{index === 0 ? 'Token Info' : ''}</TableCell>
                                        <TableCell>
                                            <div className="text-sm space-y-1">
                                                <p><strong>Created:</strong> {formatPrettyDate(token.issuedAt)}</p>
                                                <p className="text-muted-foreground">
                                                    <strong>Last Used:</strong> {token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : 'Never'}
                                                </p>
                                                <p className="text-muted-foreground"><strong>Days Left:</strong> {token.daysLeft}</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}

                            {/* Endpoints */}
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Pricing Endpoint</TableCell>
                                <TableCell>
                                    {apiData.pricing_Endpoint ? <Check className="h-5 w-5 text-green-600" /> : <X className="h-5 w-5 text-red-600" />}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Data Endpoint</TableCell>
                                <TableCell>
                                    {apiData.data_Endpoint ? <Check className="h-5 w-5 text-green-600" /> : <X className="h-5 w-5 text-red-600" />}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Enrollment Endpoint</TableCell>
                                <TableCell>
                                    {apiData.enrollment_Endpoint ? <Check className="h-5 w-5 text-green-600" /> : <X className="h-5 w-5 text-red-600" />}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Dashboard Endpoint</TableCell>
                                <TableCell>
                                    {apiData.dashboard_Endpoint ? <Check className="h-5 w-5 text-green-600" /> : <X className="h-5 w-5 text-red-600" />}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Pharmacy Endpoint</TableCell>
                                <TableCell>
                                    {apiData.pharmacy_Endpoint ? <Check className="h-5 w-5 text-green-600" /> : <X className="h-5 w-5 text-red-600" />}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Passes Endpoint</TableCell>
                                <TableCell>
                                    {apiData.pass_Endpoint ? <Check className="h-5 w-5 text-green-600" /> : <X className="h-5 w-5 text-red-600" />}
                                </TableCell>
                            </TableRow>

                            {/* URLs */}
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Implementation/UAT</TableCell>
                                <TableCell className="font-mono text-sm px-2 py-3">https://uatapi.glichealth.com/</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium border-r border-dashed px-2 py-3">Production</TableCell>
                                <TableCell className="font-mono text-sm px-2 py-3">https://api.glichealth.com/</TableCell>
                            </TableRow>

                            {/* Whitelisted IPs */}
                            {showWhitelistedSection && (
                                <TableRow>
                                    <TableCell className="font-medium border-r border-dashed px-2 py-3">
                                        Whitelisted {isDynamicIP ? "Domain" : "IP's"}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {glicIps?.map((ip) => (
                                                <div key={ip.objectUid} className="flex items-center gap-1">
                                                    <Badge variant="secondary">{ip.ipdns}</Badge>
                                                    {glicIps.length > 1 && (
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-6 w-6" 
                                                            onClick={() => removeWhiteListedIPs(ip.objectUid)}
                                                        >
                                                            <Minus className={`h-4 w-4 ${isRxTheme ? 'text-primary' : 'text-green-600'}`} />
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-8 w-8" 
                                                onClick={handleApiAddModal}
                                            >
                                                <Plus className={`h-4 w-4 ${isRxTheme ? 'text-primary' : 'text-green-600'}`} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </div>}
                    {/* Success/Error Messages */}
                    {(successIpAddress || errorIpAddress) && (
                        <p className={`text-sm ${successIpAddress ? 'text-green-600' : 'text-destructive'}`}>
                            {successIpAddress || errorIpAddress}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={closeGlicPopup}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </TooltipProvider>
    );
}

export default GlicApi;
